'use client'

import SmartCropImage from 'media/components/SmartCropImage/SmartCropImage'
import type { ProductImage } from 'product/types'
import { dataTestIds } from 'product-card/constants/dataTestIds'
import { useAnimationImagesLoad } from 'product-card/hooks/useAnimationImagesLoad'
import { useProductCard } from 'product-card/hooks/useProductCard'
import { useProductCardAnimationContext } from 'product-card/hooks/useProductCardAnimationContext'
import { useProductImageSizes } from 'product-card/hooks/useProductImageSizes/useProductImageSizes'
import { useShowAsFeatured } from 'product-card/hooks/useShowAsFeatured/useShowAsFeatured'
import { ProductCardAnimation } from 'product-card/types'
import { useCallback, useMemo, useRef } from 'react'
import { cx } from 'utils/cx'
import styles from '../ProductImage.module.scss'

interface SingleProductImageProps {
	readonly image: ProductImage
}

export const SingleProductImage = ({ image }: SingleProductImageProps) => {
	const { dataTestId, draggable, priority } = useProductCard()
	const showAsFeatured = useShowAsFeatured()

	const { animation } = useProductCardAnimationContext()

	const sizes = useProductImageSizes()
	const productImageRef = useRef<HTMLImageElement | null>(null)

	const animationImages = useMemo(() => [image], [image])
	const { isFirstImageReady, handleImageLoaded } =
		useAnimationImagesLoad(animationImages)

	// Apply fade in animation if there is any animation defined
	const fadeInAnimation = isFirstImageReady ? styles.fadeIn : styles.invisible

	const handleImageLoad = useCallback(
		() => handleImageLoaded(image),
		[image, handleImageLoaded]
	)

	return (
		<div
			className={cx(
				styles.productImage,
				animation !== ProductCardAnimation.None && fadeInAnimation
			)}
			data-testid='productCard.image'
		>
			<div
				className={styles.imageWrapper}
				data-testid='productCard.image.wrapper'
			>
				<SmartCropImage
					data-testid={dataTestIds.productCardImage(dataTestId, 0)}
					alt={image.alt}
					src={image.img}
					sizes={sizes}
					priority={priority}
					ref={productImageRef}
					showAsFeatured={showAsFeatured}
					draggable={draggable}
					fill
					onLoad={handleImageLoad}
				/>
			</div>
		</div>
	)
}
